import AccessTimeOutlined from "@mui/icons-material/AccessTimeOutlined";
import AlternateEmail from "@mui/icons-material/AlternateEmail";
import ArrowBack from "@mui/icons-material/ArrowBack";
import FlashOn from "@mui/icons-material/FlashOn";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import HubOutlined from "@mui/icons-material/HubOutlined";
import TagOutlined from "@mui/icons-material/TagOutlined";
import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import type { FC } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { AutoAwesomeColouredOutlined } from "../../../styles/icons/auto-awesome-coloured-outlined";
import type { UUID } from "../../../utils/uuid";
import { ChannelIcon } from "../../channels/components/channel-icon";
import type { ChannelName } from "../../channels/types";
import { prettyChannelName } from "../../channels/utils";
import type { ConnectionCategory } from "../../moment/hooks/use-connections";
import type { MomentFormMoment } from "../../moment/types/moment-form-moment";
import { useChannels } from "../../recipients/hooks/use-channels";
import { useRecipientsForChannel } from "../../recipients/hooks/use-recipients-for-channel";
import type { RecipientPerson } from "../../recipients/server/supabase-recipient-service";
import { useToTarget } from "../../to/use-to-target";

type SurveyAlertWizardStep6Values = Pick<
  MomentFormMoment,
  "additional_recipients" | "cc_bcc" | "channel" | "immediate" | "schedule" | "segment" | "timezone"
> & { content_prompt: string };

type SurveyAlertWizardStep6Props = {
  connectionCategories: ConnectionCategory[];
  onBack: () => void;
};

const recipientName = (recipient: RecipientPerson): string =>
  recipient.display ?? recipient.destination;

const PersonRecipientNames: FC<{ channel: ChannelName; ids: UUID[] }> = ({ channel, ids }) => {
  const { data: people } = useRecipientsForChannel(channel, false);
  const peopleById = new Map((people ?? []).map((person) => [person.id, person]));

  return ids
    .map((id) => {
      const person = peopleById.get(id);
      return person ? recipientName(person) : id;
    })
    .join(", ");
};

const ChannelRecipientNames: FC<{ channel: ChannelName; ids: UUID[] }> = ({ channel, ids }) => {
  const { channels } = useChannels(channel);
  const channelsById = new Map(
    channels.map((selectedChannel) => [selectedChannel.id, selectedChannel]),
  );

  return ids
    .map((id) => {
      const selectedChannel = channelsById.get(id);
      const name = selectedChannel?.display ?? selectedChannel?.destination ?? id;

      return `#${name.replace(/^#/, "")}`;
    })
    .join(", ");
};

type Copies = NonNullable<MomentFormMoment["cc_bcc"]>["cc"];

const CopyNames: FC<{
  channel: ChannelName;
  connectionCategories: ConnectionCategory[];
  copies: Copies;
}> = ({ channel, connectionCategories, copies }) => {
  const { data: people } = useRecipientsForChannel(channel, false);
  const peopleById = new Map((people ?? []).map((person) => [person.id, person]));
  const categoriesByKey = new Map(connectionCategories.map((category) => [category.key, category]));

  return copies
    .map((copy) => {
      if (copy.type === "dynamic") {
        return categoriesByKey.get(copy.connection)?.display_name ?? copy.connection;
      }

      const person = peopleById.get(copy.id);
      return person ? recipientName(person) : copy.id;
    })
    .join(", ");
};

export const SurveyAlertWizardStep6: FC<SurveyAlertWizardStep6Props> = ({
  connectionCategories,
  onBack,
}) => {
  const { control } = useFormContext<SurveyAlertWizardStep6Values>();
  const [
    additionalRecipients,
    ccBcc,
    channel,
    contentPrompt,
    immediate,
    schedule,
    segment,
    timezone,
  ] = useWatch({
    control,
    name: [
      "additional_recipients",
      "cc_bcc",
      "channel",
      "content_prompt",
      "immediate",
      "schedule",
      "segment",
      "timezone",
    ],
  });
  const { toTarget, target_connection_type: targetConnectionType } = useToTarget();
  const categoriesByKey = new Map(connectionCategories.map((category) => [category.key, category]));
  const primaryRecipient =
    toTarget === "employee" ? (
      (segment?.name ?? "")
    ) : toTarget === "connection" || toTarget === "manager" ? (
      `${categoriesByKey.get(targetConnectionType ?? "manager")?.display_name_plural ?? targetConnectionType ?? "Managers"} of ${segment?.name ?? ""}`
    ) : toTarget === "channel" ? (
      <>
        <ChannelRecipientNames channel={channel} ids={additionalRecipients} /> about{" "}
        {segment?.name ?? ""}
      </>
    ) : (
      <>
        <PersonRecipientNames channel={channel} ids={additionalRecipients} /> about{" "}
        {segment?.name ?? ""}
      </>
    );
  const RecipientIcon =
    toTarget === "channel"
      ? TagOutlined
      : toTarget === "notify"
        ? AlternateEmail
        : toTarget === "employee"
          ? GroupsOutlined
          : HubOutlined;

  const cc = ccBcc?.cc ?? [];
  const bcc = ccBcc?.bcc ?? [];
  const delayPeriod = schedule?.interval_json ? Object.keys(schedule.interval_json)[0] : undefined;
  const delayAmount = delayPeriod
    ? schedule?.interval_json?.[delayPeriod as keyof typeof schedule.interval_json]
    : undefined;
  const formattedDelayPeriod = delayAmount === 1 ? delayPeriod?.replace(/s$/, "") : delayPeriod;

  return (
    <>
      <DialogTitle>
        <Stack spacing={1} alignItems="flex-start">
          <Button
            startIcon={<ArrowBack />}
            onClick={onBack}
            data-analytics-id="survey-alert-wizard-back"
          >
            Back
          </Button>
          <Typography variant="h6" component="span">
            Review and confirm
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={4}>
          <Stack spacing={2}>
            <Typography>Check that the details below are correct.</Typography>
            <Typography>
              In the next step, you can text your Communication before publishing - take care not to
              remove the survey question and response tags.
            </Typography>
          </Stack>
          {channel && (
            <Stack spacing={1}>
              <Typography variant="overline" color="text.secondary">
                Channel
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <ChannelIcon channel={channel} fontSize="medium" />
                <Typography>{prettyChannelName(channel)}</Typography>
              </Stack>
            </Stack>
          )}
          <Stack spacing={1}>
            <Typography variant="overline" color="text.secondary">
              Recipients
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <RecipientIcon color="primary" fontSize="medium" />
              <Stack spacing={0.5}>
                <Typography>{primaryRecipient}</Typography>
                {cc.length > 0 && (
                  <Typography color="text.secondary">
                    CC:{" "}
                    <CopyNames
                      channel={channel}
                      connectionCategories={connectionCategories}
                      copies={cc}
                    />
                  </Typography>
                )}
                {bcc.length > 0 && (
                  <Typography color="text.secondary">
                    BCC:{" "}
                    <CopyNames
                      channel={channel}
                      connectionCategories={connectionCategories}
                      copies={bcc}
                    />
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Stack>
          <Stack spacing={1}>
            <Typography variant="overline" color="text.secondary">
              Schedule
            </Typography>
            {immediate ? (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <FlashOn color="primary" fontSize="medium" />
                <Typography>
                  Send the notification immediately when a survey is submitted
                </Typography>
              </Stack>
            ) : (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <AccessTimeOutlined color="primary" fontSize="medium" />
                <Typography>
                  Send {delayAmount} {formattedDelayPeriod} after a survey is completed at{" "}
                  {schedule?.send_at} ({timezone ?? "Recipients’ local time"})
                </Typography>
              </Stack>
            )}
          </Stack>
          <Stack spacing={1}>
            <Typography variant="overline" color="text.secondary">
              Prompt
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <AutoAwesomeColouredOutlined />
              <Typography>{contentPrompt || "(Not provided)"}</Typography>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
    </>
  );
};
